// Import React and hooks for managing component state and lifecycle
import React, { useState, useEffect } from 'react';
// Import React Router hooks for URL parameters and navigation
import { useParams, useNavigate } from 'react-router-dom';
// Import custom hook to get the current database user information
import { useDbUser } from '../hooks/useDbUser';
// Import database functions and TypeScript types for data structures
import { db, Client, Project, Matter, Goal, Task, Document } from '../lib/db';
import { 
  ArrowLeft,
  Building2,
  FolderOpen,
  Folder,
  Target,
  CheckSquare,
  Calendar,
  AlertCircle,
  Clock,
  FileText
} from 'lucide-react'; // Import icons from the Lucide icon library
// Import toast notifications for user feedback
import { toast } from 'sonner';
// Import component for displaying list of documents
import DocumentList from '../components/documents/DocumentList';

// TypeScript interface defining the structure of client data
// This groups all data related to a specific client
interface ClientData {
  client: Client;
  projects: Project[];
  matters: Matter[];
  deliverables: Goal[];
  tasks: Task[];
  documents: Document[];
}

/**
 * Client Dashboard Page Component
 * 
 * This page displays comprehensive information about a specific client including:
 * - Client basic information (name, company)
 * - Statistics: number of projects, matters, deliverables, and active tasks
 * - Task summary with overdue, due today, and in-progress counts
 * - List of all client's projects with status
 * - List of all client's deliverables with progress tracking
 * - List of all documents associated with the client
 * 
 * Navigation: Accessible via /clients/:clientId route
 * Users can click back to return to the main clients page
 */
function ClientDashboard() {
  // Extract clientId from the URL parameters (from route like /clients/123)
  const { clientId } = useParams<{ clientId: string }>();
  // Get current user's database ID and loading state
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  // Hook for programmatic navigation to other pages
  const navigate = useNavigate();
  // State to track if we're currently loading client data from the database
  const [loading, setLoading] = useState(true);
  // State to hold all the client's data (projects, tasks, documents, etc.)
  // Starts as null until data is loaded from the database
  const [clientData, setClientData] = useState<ClientData | null>(null);

  // Effect to load client data when component mounts or dependencies change
  // Waits for user authentication and clientId to be available
  useEffect(() => {
    if (dbUserId && clientId && !dbUserLoading) {
      loadClientData();
    }
  }, [dbUserId, clientId, dbUserLoading]);


  /**
   * Loads all data related to the client from the database
   * 
   * This function:
   * 1. Fetches the client's basic information
   * 2. Loads all user's data (projects, matters, deliverables, tasks, documents)
   * 3. Filters everything to show only items related to this specific client
   * 4. Creates relationships between different data types
   * 5. Handles document associations to show client-related documents
   */
  const loadClientData = async () => {
    if (!dbUserId || !clientId) return;
    
    setLoading(true);
    try {
      // First, load the specific client's basic information (name, company, etc.)
      // If client doesn't exist, show error and redirect back to clients page
      const client = await db.clients.findById(clientId, dbUserId);
      if (!client) {
        toast.error('Client not found');
        navigate('/clients');
        return;
      }

      // Load ALL user data from database in parallel for efficiency
      // We'll filter this data to show only items related to this client
      const [allProjects, allMatters, allDeliverables, allTasks, allDocuments] = await Promise.all([
        db.projects.findByUserId(dbUserId),
        db.matters.findByUserId(dbUserId),
        db.goals.findByUserId(dbUserId, false),
        db.tasks.findByUserId(dbUserId),
        db.documents.findByUserId(dbUserId)
      ]);

      // Now filter all the loaded data to show only items belonging to this client
      // Projects: direct client_id match
      const projects = allProjects.filter(p => p.client_id === clientId);
      const projectIds = new Set(projects.map(p => p.id));
      
      // Matters: either directly linked to client OR linked to one of client's projects
      const matters = allMatters.filter(m => 
        m.client_id === clientId || (m.project_id && projectIds.has(m.project_id))
      );
      const matterIds = new Set(matters.map(m => m.id));
      
      // Deliverables (goals): linked to client, client's projects, OR client's matters
      const deliverables = allDeliverables.filter(d => 
        d.client_id === clientId || 
        (d.project_id && projectIds.has(d.project_id)) ||
        (d.matter_id && matterIds.has(d.matter_id))
      );
      const deliverableIds = new Set(deliverables.map(d => d.id));
      
      // Tasks: must belong to one of the client's deliverables AND not be completed
      const tasks = allTasks.filter(t => 
        t.goal_id && deliverableIds.has(t.goal_id) && t.status !== 'Complete'
      );

      // Documents require special handling because they use association tables
      // We need to check document associations to find client-related documents
      const documents = allDocuments.filter(doc => {
        // Check if document has an association with this client
        return doc.id; // We'll filter based on associations later
      });

      // Load the association table that links documents to clients/projects/matters
      // Then filter documents that have associations with this client's entities
      const associations = await db.documentAssociations.findByUserId(dbUserId);
      const clientDocuments = allDocuments.filter(doc => {
        const docAssociations = associations.filter(a => a.document_id === doc.id);
        return docAssociations.some(a => 
          a.client_id === clientId ||
          (a.matter_id && matterIds.has(a.matter_id)) ||
          (a.project_id && projectIds.has(a.project_id))
        );
      });

      setClientData({
        client,
        projects,
        matters,
        deliverables,
        tasks,
        documents: clientDocuments
      });
    } catch (error) {
      console.error('Error loading client data:', error);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculates task statistics for the dashboard summary
   * 
   * Returns an object with counts for:
   * - total: total number of active tasks
   * - overdue: tasks with deadline in the past
   * - dueToday: tasks with deadline today
   * - inProgress: tasks currently being worked on
   */
  const getTaskStats = () => {
    if (!clientData) return { total: 0, overdue: 0, dueToday: 0, inProgress: 0 };
    
    // Get today's date at midnight for accurate deadline comparisons
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const stats = {
      total: clientData.tasks.length,
      overdue: 0,
      dueToday: 0,
      inProgress: 0
    };
    
    // Loop through each task to calculate statistics
    clientData.tasks.forEach(task => {
      if (task.status === 'In Progress') stats.inProgress++;
      
      if (task.deadline) {
        const deadline = new Date(task.deadline);
        if (deadline < today) {
          stats.overdue++;
        } else if (deadline >= today && deadline < tomorrow) {
          stats.dueToday++;
        }
      }
    });
    
    return stats;
  };

  /**
   * Returns CSS class for priority color coding
   * Critical = red, High = orange, Medium = yellow, Low = green
   */
  const getPriorityColor = (priority: string | undefined) => {
    switch (priority) {
      case 'Critical': return 'text-red-600';
      case 'High': return 'text-orange-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  // Show loading spinner while authenticating user or loading client data
  if (dbUserLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading client dashboard...</div>
      </div>
    );
  }

  // Show error message if client data couldn't be loaded
  if (!clientData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Client not found</div>
      </div>
    );
  }

  const taskStats = getTaskStats();

  // Render the complete client dashboard with all sections
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </button>
        
        <div className="flex items-center">
          <Building2 className="w-8 h-8 text-indigo-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{clientData.client.name}</h1>
            {clientData.client.company && (
              <p className="text-gray-600">{clientData.client.company}</p>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards: Projects, Matters, Deliverables, Active Tasks */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Projects</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{clientData.projects.length}</p>
            </div>
            <FolderOpen className="w-8 h-8 text-indigo-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Matters</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{clientData.matters.length}</p>
            </div>
            <Folder className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Deliverables</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{clientData.deliverables.length}</p>
            </div>
            <Target className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Tasks</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{taskStats.total}</p>
            </div>
            <CheckSquare className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Task Summary: Overdue, Due Today, In Progress counts */}
      {taskStats.total > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Summary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-sm text-gray-600">Overdue:</span>
              <span className="ml-2 font-semibold text-red-600">{taskStats.overdue}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-blue-500 mr-2" />
              <span className="text-sm text-gray-600">Due Today:</span>
              <span className="ml-2 font-semibold text-blue-600">{taskStats.dueToday}</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-600">In Progress:</span>
              <span className="ml-2 font-semibold text-green-600">{taskStats.inProgress}</span>
            </div>
          </div>
        </div>
      )}

      {/* Projects Section: List all client's projects with status */}
      {clientData.projects.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Projects</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {clientData.projects.map((project) => (
              <div 
                key={project.id} 
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate('/projects')} // Navigate to projects page when clicked
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{project.title}</h3>
                    {project.description && (
                      <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                    )}
                  </div>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                    {project.status || 'Active'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deliverables Section: List all client's deliverables with progress */}
      {clientData.deliverables.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Deliverables</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {clientData.deliverables.map((deliverable) => {
              // Find all tasks that belong to this deliverable
              const tasks = clientData.tasks.filter(t => t.goal_id === deliverable.id);
              // Count how many tasks are completed to show progress percentage
              const completedTasks = tasks.filter(t => t.status === 'Complete').length;
              
              return (
                <div 
                  key={deliverable.id} 
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate('/dashboard')} // Navigate to main dashboard when clicked
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{deliverable.title}</h3>
                      {deliverable.description && (
                        <p className="text-sm text-gray-600 mt-1">{deliverable.description}</p>
                      )}
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        {/* Show priority with color coding (1=Critical, 2=High, 3=Medium, 4+=Low) */}
                        <span className={getPriorityColor(`${deliverable.priority === 1 ? 'Critical' : deliverable.priority === 2 ? 'High' : deliverable.priority === 3 ? 'Medium' : 'Low'}`)}>
                          Priority {deliverable.priority}
                        </span>
                        <span className="mx-2">•</span>
                        {/* Show total number of tasks in this deliverable */}
                        <span>{tasks.length} tasks</span>
                        {tasks.length > 0 && (
                          <>
                            <span className="mx-2">•</span>
                            {/* Show completion percentage based on completed vs total tasks */}
                            <span>{Math.round((completedTasks / tasks.length) * 100)}% complete</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Documents Section: List all documents associated with this client */}
      {clientData.documents.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
            <div className="flex items-center text-sm text-gray-500">
              <FileText className="w-4 h-4 mr-1" />
              <span>{clientData.documents.length} document{clientData.documents.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="p-6">
            {/* Render the document list component with client's documents */}
            <DocumentList
              documents={clientData.documents}
              dbUserId={dbUserId!}
              showFolders={false}
              compact={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDashboard;